// SaaS tenant config stub — minimal subset of Core's tenant config.
// Core dùng VITE_TENANT_ID + tenants/_default + per-tenant overrides để branding theo khách.
// SaaS multi-tenant runtime — tenant context xác định qua JWT/membership chứ KHÔNG qua build-time env.
// File này export 1 stub object để pages port từ Core không bị Vite "Failed to resolve".

const tenant = {
  id: 'cnv-saas',
  app_name: 'CNV Work',
  app_short: 'CNV Work',

  branding: {
    logo: '/logo.png',
    primary_color: '#2563eb',
    login_subtitle: 'Hệ thống quản trị doanh nghiệp All in one\nCRM - OPERATION - GROWTH',
  },

  modules: {
    dashboard: true,
    employees: true,
    attendance: true,
    crm: true,
    tasks: true,
    approvals: true,
    assets: true,
    recruitment: true,
    automation: true,
    marketing: true,
    fulfillment: false,
    etelecom: false,
    domains: false,
  },

  // team-chat (module nội bộ Slack/Lark) — BẬT cho cả SaaS lẫn Core (cả 2 đều có /chat).
  // Gate `tenant.private_modules?.team_chat` ở AppearanceSection (mục "Tin nhắn nội bộ":
  // theme chat + Cỡ chữ + cách hiện tin). Thiếu key này → section bị ẩn ở mọi env.
  private_modules: {
    team_chat: true,
  },

  features: {
    // Nút "Xem / In" (báo giá + đơn bán hàng) mở link PUBLIC cho khách xem/in.
    // Core: mở thẳng `/inv/<short-id uuid>` — single-tenant, resolve bằng prefix uuid.
    // SaaS: multi-tenant + RLS + PII khách → prefix uuid dò được và không map được tenant,
    // nên phải xin share token 32 hex trước rồi mở `/inv/<token>` (ADR-032).
    // Gate `=== true`: tenant.js của Core KHÔNG có key này → Core giữ nguyên hành vi cũ,
    // KHÔNG phải sửa 1 dòng nào.
    share_link_token: true,
    // AI Agent tự tạo báo giá / đơn bán hàng khi khách chốt (07/10/2026) — máy chủ (aiSalesDocService + cột
    // chat_agents.auto_order_*) CHỈ Work có. Gate `=== true`: tenant.js của Core KHÔNG có key ⇒ Core ẩn thẻ cài
    // đặt ở trang AI Agent. Connect bọc stub này nhưng không có Hộp thư ⇒ vô hại.
    ai_auto_sales_doc: true,
    // Nút "Xem / In" của NHÂN VIÊN mở bản in NỘI BỘ `/print/q|inv/<id>` (đường đã đăng nhập, máy chủ gác
    // bản ghi — `GET /crm/{quotations,invoices}/:id/print-view`) thay vì xin link công khai rồi mở
    // nó. Trước 01/10/2026 Work mở CHÍNH link gửi khách ⇒ mỗi lần nhân viên xem/in là cộng một lượt vào
    // «Khách đã xem N lần», và chứng từ nháp tự có link công khai. Làm theo Core (06/09/2026).
    // Connect bọc stub này (@kernel/tenant) nên cũng mang cờ — vô hại: CRM của Connect tắt, không có nút "Xem / In".
    share_link_internal_print: true,
    google_drive: false,
    // Kết nối Google Drive OAuth (drive.file) + chế độ tạo tài liệu byok/oauth ở /settings/google.
    // BE endpoint /crm/doc-templates/google/* CHỈ có ở SaaS — Core không có nên khối này là
    // dead-UI ở Core. Gate `=== true`: tenant.js của Core KHÔNG có key này → Core tự ẩn.
    google_drive_oauth: true,
    // Ghi âm/tóm tắt cuộc họp bằng Fireflies.ai — TẮT ở SaaS (2026-07-11), Core vẫn dùng.
    // Gate ở ActivityForm (toggle "Ghi âm cuộc họp") — Core không có key này → default BẬT.
    meeting_recording: false,
    // Gợi ý tiêu đề cuộc họp "<Công ty> - Tư vấn giải pháp CDP" (CNV) — TẮT ở SaaS
    // (2026-07-11) → placeholder chỉ hiển thị "Cuộc họp". Core default BẬT.
    meeting_subject_suggest: false,
    // "Kích hoạt sản phẩm" trên đơn bán hàng/thanh toán — provision license/app/credit khi
    // TT duyệt. ĐÚNG mô hình Core (CNV bán license) nhưng KHÔNG hợp SaaS đa mô hình khách
    // (2026-07-18, sếp Phú duyệt). Engine BE đã gỡ; flag này ẩn nốt VỎ FE (nút "Kích hoạt SP",
    // panel ActivateItems, badge "Đã kích hoạt/1 phần", "→ Tự kích hoạt"). Gate `!== false`:
    // Core KHÔNG có key → default BẬT, KHÔNG đụng Core. Chỉ SaaS ẩn.
    crm_activation: false,
    // Mẫu hợp đồng lao động = link Google Docs (copy + điền {{biến}} như tài liệu CRM) thay cho
    // upload file tĩnh. SaaS: file upload vốn không lưu được và file tĩnh thì không điền được
    // thông tin nhân viên. Core vẫn upload thật + dùng chung trang này → gate `=== true`:
    // tenant.js của Core KHÔNG có key này nên giữ nguyên khối upload, không phải sửa 1 dòng.
    contract_doc_template: true,
    // Banner "AI không hoạt động vì key hết quota" trong inbox + trang Tích hợp ChatGPT/Claude.
    // Vì sao cần: key hết quota → provider trả 429 → bot im, mà UI vẫn hiện "AI đang phản hồi
    // khách hàng" → người trực tưởng bot đang làm việc (sự cố prod 2026-07-29: 40 lượt
    // openai_429 liên tiếp, không dấu vết nào trên màn hình). BE endpoint
    // GET /inbox/ai/key-status CHỈ có ở SaaS → gate `=== true`: tenant.js của Core KHÔNG có key
    // này nên Core ẩn hẳn, không gọi API, không phải sửa gì.
    ai_key_health: true,
    // Đơn/báo giá NHIỀU loại: đánh dấu Bán mới/Upsell/Gia hạn theo TỪNG SẢN PHẨM (Core,
    // 07.08.2026). BE SaaS chưa có cột `crm_invoice_items.item_type` + `invoice_types` nên
    // bật ở đây thì user đánh dấu xong F5 là mất (BE nuốt field) — tệ hơn là không có.
    // Gate `!== false`: tenant.js của Core KHÔNG có key này → Core mặc định BẬT, không phải sửa gì.
    mixed_invoice_types: false,
    // «Gọi người vào giúp» — nhóm chat nhận card 🆘 (chung | theo loại ticket), Inbox → Cài đặt chat.
    // Core (`admin/src/tenants/_default/config.js`) + Work bật; Connect tắt Hộp thư nên không khai.
    // Gate `=== true`: host nào không khai thì khối tự ẩn, không gọi API.
    escalate_notify_room: true,
    // Cài đặt Hộp thư «Chưa liên kết khách hàng thì KHÔNG chat được — chỉ bật/tắt AI» (30/09/2026).
    // BE GET/PUT /inbox/crm-link-gate + cờ `crm_link_required` ở /conversations/:id/categories CHỈ
    // có ở SaaS (kernel inboxCrmLinkGate). Gate `=== true`: tenant.js Core KHÔNG có key ⇒ khối ẩn.
    inbox_crm_link_gate: true,
    // "Áp dụng cho" (Tất cả / CNV Holdings / đối tác) khi tạo KM — CNV-only: đánh dấu KM cho nội bộ
    // connect.cnvwork.com vs đối tác, gắn với Partner API catalog (addon CHỈ có ở Core). SaaS chưa
    // có partner-api catalog nên TẮT. Gate `!== false`: tenant.js Core KHÔNG có key → Core mặc định BẬT.
    partner_promotion_scope: false,
    // Chủ dữ liệu CNV / đối tác VGs (`data_org` trên bản ghi CRM) — CHỈ Core trả cột này
    // (attendance-app backend utils/dataOrg.js). SaaS không có ⇒ TẮT, kẻo admin (quyền '*') thấy
    // cột «Chủ dữ liệu» trống. Gate `!== false`: tenant.js Core KHÔNG có key → Core mặc định BẬT.
    crm_data_org: false,
    // Trang Xuất file (Export Center, Tiện ích › Xuất file) + quyền `exports:*` — CHỈ Work có (docs/EXPORT_CENTER.md).
    // Nút tải tệp thẳng (vd «Xuất Excel» Liên hệ / Công ty — components/crm/CrmListExportButton) đòi thêm tầng 2
    // `exports:view` như máy chủ Work đòi. Gate `=== true`: tenant.js Core KHÔNG có key → Core không đòi quyền đó.
    export_center: true,
    // Kho tri thức nhận tệp BẢNG (.xlsx/.xls/.csv) → `doc_kind='catalog'`: giữ nguyên hàng tiêu
    // đề, mỗi dòng một bản ghi, khai được cột CẤM báo khách (giá đại lý). CHỈ có ở SaaS —
    // `kbUpload.fileFilter` của Core vẫn từ chối .xlsx ("Chỉ chấp nhận file PDF, Markdown, Text").
    // Không gác thì ô chọn tệp ở Core cho người ta chọn Excel rồi BE trả lỗi sau khi đã tải lên
    // xong — đúng lớp lỗi FE/BE lệch mà `check-upload-policy-drift.js` sinh ra để chặn.
    // Gate `=== true`: tenant.js của Core KHÔNG có key này → Core giữ nguyên hành vi cũ.
    kb_catalog: true,
    // Thẻ «Website» của kho tri thức: ô «Lấy cả các trang con cùng website» + nhãn «Trang con: N» (27/10/2026). Máy
    // chủ Work có bộ tải trang con (kbSourceLoader — tối đa 50 trang, theo robots.txt); Core `parseURL` chỉ tải MỘT
    // trang và không đọc `crawl_subpages` ⇒ hiện ô đó ở Core là ô tick không làm gì. Gate `=== true`: Core không có key.
    kb_website_subpages: true,
    // Tài sản (01/10/2026, HR nhập sổ tài sản — sếp Phú: «nhà cung cấp phải chọn từ kho nhà cung cấp»):
    //   • asset_supplier — ô «Nhà cung cấp» ở form + trang chi tiết tài sản, chọn từ kho Tiện ích › Nhà cung
    //     cấp qua GET /assets/supplier-options; ghi vào cột `assets.supplier_id`.
    //   • asset_import  — nút «Nhập từ tệp» = popup chung ImportDataModal trên /assets/import/{spec,template,
    //     preview,commit} (soát trước, ghi sau).
    // BẬT 01/10/2026: Work + Connect (Connect kế thừa stub này qua lớp bọc tenant) đã có cột `supplier_id`
    // (migration `tai-san-nha-cung-cap`) + `/assets/supplier-options` + 4 đường nhập — chữ của popup nhập theo
    // ngôn ngữ người dùng. ⚠️ Hệ nào gỡ BE thì TẮT lại ở đây (hoặc đè ở tenant.js của hệ đó): bật mà BE chưa có
    // thì ô NCC lưu xong F5 là mất (BE nuốt field), nút nhập ra lỗi. Gate `!== false`: tenant.js của Core KHÔNG
    // có key ⇒ Core mặc định BẬT.
    asset_supplier: true,
    asset_import: true,
    // Nút «Nhập file» ở trang Đơn bán hàng (12/10/2026, TC-983/984 — khách chuyển đơn LỊCH SỬ từ hệ cũ) = popup chung
    // ImportDataModal trên /crm/invoices/import/{spec,template,preview,commit}: soát theo ĐƠN, ghi IM LẶNG (không thẻ
    // «Tiền về», không tự động hoá, không hoa hồng). CHỈ Work có 4 đường đó (api crm/routes/v1.js + kernel 0.166.0
    // invoiceImportSpec). Gate `=== true`: tenant.js Core KHÔNG có khoá ⇒ nút ẩn ở Core. Connect kế thừa stub này nhưng
    // CRM của Connect đang ẩn khỏi menu (trang không tới được) — hệ nào bật CRM mà chưa có route thì đè `false` ở đó.
    crm_invoice_import: true,
    // Trang «Lịch sử chấm công» `/my-attendance` (check-in/out + gửi giải trình) CHỈ Core có — Work và Connect
    // không mount route đó, bấm vào là rơi về trang chủ (đo 01/10/2026 khi viết hướng dẫn huongdan.cnvwork.com).
    // Tắt ⇒ dòng «Lịch sử chấm công» ở Cài đặt cá nhân ẩn đi, băng «Quên check-out» trong Chat mở thẳng
    // popup đơn bổ sung chấm công (đã điền ngày) thay vì đi trang chết. Nhân viên Work/Connect xem
    // bảng công của mình trên app. Gate `!== false`: tenant.js Core KHÔNG có key ⇒ Core giữ nguyên.
    my_attendance_page: false,
    // Lương — ẨN 07/10/2026 ở Work (sếp Phú: «ẩn tính năng liên quan lương trên saas đi, hệ này làm chưa xong»).
    // Work gỡ menu «Lương» + route /payroll/* + /profile/payroll (cnvwork-saas admin AppLayout/App.jsx); cờ này ẩn
    // nốt lối tắt «Bảng lương cá nhân» ở Cài đặt cá nhân (bấm vào là rơi trang chết). Connect kế thừa — Connect vốn
    // tắt Lương. Gate `!== false`: tenant.js Core KHÔNG có key ⇒ Core giữ nguyên. Bật lại Work ⇒ xoá dòng này.
    payroll: false,
    // Đường trang «Gói & Thanh toán» của hệ (route SPA) — link «Xem gói & mua thêm» ở banner hết số kênh Hộp thư
    // (ChatChannelsPage). Work + Connect (kế thừa qua lớp bọc tenant) đều mount `/billing` → MarketplacePage. Core
    // KHÔNG có trang mua gói ⇒ tenant.js Core không khai ⇒ link ẩn (cổng check-dead-links của Core đỏ 14/10/2026 vì
    // link ghi cứng). Hệ nào đổi đường trang mua gói thì đè khoá này ở tenant.js của hệ đó.
    billing_path: '/billing',
  },

  emails: {
    from_name: 'CNV Work',
    reply_to: '',
    support: '',
    recruitment: '',
  },

  defaults: {
    currency: 'VND',
    timezone: 'Asia/Ho_Chi_Minh',
  },
};

export default tenant;
